import API_URLS from "@/constants/apiUrls";
import { HTTP_METHODS } from "@/constants/httpMethods";
import { request } from "@/lib/api";

/**
 * Hotel Basic Service
 * Temel otel API'leri için servis
 */
export const hotelBasicService = {
  /**
   * Sayfa tipini belirle
   * @param {Array} queries - URL segment'leri (örn: ["istanbul-ankara", "2025-10-18"])
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Examples:
   * decidePageType(["istanbul-ankara", "2025-10-18"]) 
   * → GET /decidePageType?query=istanbul-ankara&query=2025-10-18
   */
  decidePageType: (queries = [], headers = {}, hasErrorHandler = false) => {
    const queryString = queries.map(q => `query=${encodeURIComponent(q)}`).join('&');
    return request({
      method: HTTP_METHODS.GET,
      url: `${API_URLS.hotel.basic.decidePageType}?${queryString}`,
      payload: {},
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Tüm kampanyaları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "CurrencyCode": "TRY"
   * }
   */
  getAllCampaigns: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.basic.getAllCampaigns,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Tüm ayrıcalıkları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Payload Example:
   * {
   *   "LanguageCode": "tr"
   * }
   */
  getAllPrivileges: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.basic.getAllPrivileges,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Blog yazılarını getir (otel ana sayfa seyahat yazıları)
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "TR", "EN")
   * @param {string} payload.Slug - İçerik slug (örn: "otel")
   * @param {number} payload.BlogType - Blog tipi (örn: 1)
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise<{ BlogPostWrapper?: { Header?, Subtitle?, BlogPosts: Array } }>} API response
   * 
   * Payload Example:
   * { "LanguageCode": "TR", "Slug": "otel", "BlogType": 1 }
   */
  getBlog: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.basic.getBlog,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Sık sorulan soruları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "CurrencyCode": "TRY"
   * }
   */
  getSss: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.basic.getSss,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Slug'a göre içerik getir
   * @param {Object} payload - API payload
   * @param {string} payload.Slug - Slug değeri (örn: "akra-antalya")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Payload Example:
   * {
   *   "Slug": "akra-antalya"
   * }
   */
  getContentBySlug: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.basic.getContentBySlug,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Breadcrumb bilgilerini getir
   * @param {Object} payload - API payload
   * @param {string} payload.ViewType - Görünüm tipi (örn: "hotel")
   * @param {string} payload.Slug - Slug değeri (örn: "erguvan-hotel-special-class")
   * @param {string} payload.LanguageCode - Dil kodu (örn: "TR", "EN")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Payload Example:
   * {
   *   "ViewType": "hotel",
   *   "Slug": "erguvan-hotel-special-class",
   *   "LanguageCode": "TR"
   * }
   */
  getBreadcrumb: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.basic.getBreadcrumb,
      payload,
      headers,
      hasErrorHandler,
    }),
};

